import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getAccessToken, getCompanyId } from "@lhd/utility";
import { notification } from "antd";
import { IndustryCategory } from "@lhd/types";
import { AxiosResponse } from "axios";
import { isArray } from "lodash";
import { setSalaryPermission } from "@lhd/app/slices";
import { useDispatch } from "react-redux";

export const useFetchConfigs = () => {
  return useQuery(
    [QUERY_NAME.CONFIGS],
    () =>
      axios({
        method: "get",
        url: SERVICES.CONFIGS,
      }),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useFetchTableMetaConfigs = () => {
  return useQuery(
    [QUERY_NAME.TABLE_META_CONFIGS],
    () =>
      axios({
        method: "get",
        url: SERVICES.TABLE_META_CONFIGS,
      }).then((res) => res.data as any),
    {
      refetchOnWindowFocus: false,
      cacheTime: 500,
      staleTime: 500,
    },
  );
};

export const useSalaryPermission = (enabledOverride = true) => {
  const companyId = getCompanyId();
  const accessToken = getAccessToken();
  const dispatch = useDispatch();
  return useQuery(
    [QUERY_NAME.SALARY_PERMISSIONS, companyId],
    () =>
      axios({
        method: "post",
        url: SERVICES.SALARY_PERMISSIONS,
        params: {
          company_id: companyId,
        },
      }).then((res) => res.data as any),
    {
      refetchOnWindowFocus: false,
      enabled: enabledOverride && !!companyId && !!accessToken,
      onSuccess: (data) => {
        dispatch(setSalaryPermission(data));
      },
      onError: () => {
        dispatch(setSalaryPermission({}));
      },
    },
  );
};

//config tax
export const useFetchConfigsTax = () => {
  const company_id = getCompanyId();
  return useQuery<AxiosResponse<IndustryCategory[]>>(
    [QUERY_NAME.CONFIG_TAX],
    () =>
      axios({
        method: "get",
        url: SERVICES.INDUSTRY_CATEGORIES,
        params: { company_id },
      }),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useFetchConfigsTaxByCompany = (target_month_date?: string) => {
  const company_id = getCompanyId();
  return useQuery<AxiosResponse<IndustryCategory>>(
    [QUERY_NAME.CONFIG_TAX + "_by_company", company_id, target_month_date],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.COMPANIES}/${company_id}/company_tax_configs`,
        params: { company_id, target_month_date },
      }),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!target_month_date,
    },
  );
};

export const useSubmitConfigTax = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, industryCategoryId, industryCategoryTaxConfigId, cb }: any) => {
      const url = `${SERVICES.INDUSTRY_CATEGORIES}/${industryCategoryId}${
        SERVICES.INDUSTRY_CATEGORY_TAX_CONFIGS
      }${industryCategoryTaxConfigId ? `/${industryCategoryTaxConfigId}` : ""}`; //industryCategoryTaxConfigId is falsy => create new else is update

      const company_id = getCompanyId();
      return axios({
        method: industryCategoryTaxConfigId ? "put" : "post",
        url,
        data: payload,
        params: { company_id },
      });
    },
    {
      onSuccess(data, variables) {
        queryClient.setQueryData([QUERY_NAME.CONFIG_TAX], (oldData: any) => {
          return {
            ...data,
            industryCategoryIdPreUpdate: variables.industryCategoryId,
          };
        });
        notification["success"]({
          message: "保存しました",
        });
        if (variables.cb) variables.cb();
      },
      onError(e: any) {
        queryClient.setQueryData([QUERY_NAME.CONFIG_TAX], (oldData: any) => {
          return { ...oldData };
        });
        if (isArray(e?.response?.data?.error)) {
          e?.response?.data?.error.forEach((message: string) => {
            notification["error"]({
              message,
            });
          });
        } else {
          notification["error"]({
            message: "保存に失敗しました。",
          });
        }
      },
    },
  );
};

export const useDeleteConfigTax = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, industryCategoryId, industryCategoryTaxConfigId, cb }: any) => {
      const url = `${SERVICES.INDUSTRY_CATEGORIES}/${industryCategoryId}${SERVICES.INDUSTRY_CATEGORY_TAX_CONFIGS}/${industryCategoryTaxConfigId}`; //industryCategoryTaxConfigId is falsy => create new else is update
      const company_id = getCompanyId();
      return axios({
        method: "delete",
        url,
        data: payload,
        params: { company_id },
      });
    },
    {
      onSuccess(data, variables) {
        queryClient.setQueryData([QUERY_NAME.CONFIG_TAX], (oldData: any) => {
          return {
            ...data,
            industryCategoryIdPreUpdate: variables.industryCategoryId,
          };
        });
        variables.cb && variables.cb();
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(e: any) {
        queryClient.setQueryData([QUERY_NAME.CONFIG_TAX], (oldData: any) => {
          return { ...oldData };
        });

        if (isArray(e?.response?.data?.error)) {
          e?.response?.data?.error.forEach((message: string) => {
            notification["error"]({
              message,
            });
          });
        } else {
          notification["error"]({
            message: "保存に失敗しました。",
          });
        }
      },
    },
  );
};
